import React from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Brain, Home, Users, Baby, Flame, Briefcase, BookOpen, DoorOpen } from "lucide-react";

const CASES = [
  {
    icon: Home,
    title: "Remote & Hybrid Workers",
    body: "Working from home means no physical boundary between flow and family. WYLO restores the structural cues an office used to give for free - without watching you do it.",
    note: "Bloom et al. found that homeworkers experienced a 13% performance gain - but also greater isolation and harder transitions between modes (Bloom, N. et al., 2015, Quarterly Journal of Economics, 130(1)).",
    color: "#2C6BAC",
  },
  {
    icon: Briefcase,
    title: "Entrepreneurs & Founders",
    body: "Context-switching is the silent tax on early-stage operators. WYLO surfaces the cost of every switch so you can choose where to spend your day, not have it spent for you.",
    note: "Rapid task-switching imposes measurable performance costs: response time and accuracy decline as switches accumulate (Monsell, S., 2003, Trends in Cognitive Sciences, 7(3), 134–140).",
    color: "#1A3A5C",
  },
  {
    icon: Brain,
    title: "Neurodivergent Professionals (ADHD)",
    body: "Built as a structural support tool for attention regulation. ADHD brains pay a steeper switching tax - WYLO becomes the gentle return-to-task you can rely on.",
    note: "Executive function differences in ADHD - including working memory and shifting - are well documented (Diamond, A., 2013, Annual Review of Psychology, 64, 135–168; Barkley, R., 1997, Psychological Bulletin, 121, 65–94).",
    color: "#BA7517",
  },
  {
    icon: Baby,
    title: "New Mothers · Postpartum Cognitive Change",
    body: "Postpartum brain fog is real, well-documented, and temporary. WYLO bridges the gap between intention and follow-through, with no performance pressure and no judgement.",
    note: "Pregnancy produces measurable structural changes in regions linked to social cognition and theory of mind, persisting for at least two years (Hoekzema, E. et al., 2017, Nature Neuroscience, 20(2), 287–296).",
    color: "#1D9E75",
  },
  {
    icon: Flame,
    title: "Menopause & Brain Fog",
    body: "The SWAN study and broader literature confirm what too many women already know - cognition shifts during perimenopause and menopause. WYLO is a tool, not a treatment, but it helps.",
    note: "The Study of Women's Health Across the Nation (SWAN) demonstrated decrements in processing speed and verbal memory during the menopause transition (Greendale, G.A. et al., 2009, Neurology, 72(21), 1850–1857).",
    color: "#E24B4A",
  },
  {
    icon: Users,
    title: "Caregivers & High-Demand Home Environments",
    body: "Cognitive load research consistently identifies caregiving as one of the heaviest attentional tasks there is. WYLO holds your place in your work while life makes its demands.",
    note: "Chronic caregiving is associated with elevated cortisol and decrements in attention and executive function (Vitaliano, P.P. et al., 2003, Psychological Bulletin, 129(6), 946–972; Caswell, L.W. et al., 2003, Journal of Gerontology).",
    color: "#2C6BAC",
  },
  {
    icon: DoorOpen,
    title: "Open-Door Office Environments",
    body: "Walk-in interruptions are a feature of healthy culture and an enemy of deep work. WYLO logs them as real events, so leaders can rebalance access with focus.",
    note: "Open-plan offices reduce face-to-face interaction by roughly 70% and increase electronic communication - a counter-intuitive finding with measurable productivity cost (Bernstein, E. & Turban, S., 2018, Philosophical Transactions of the Royal Society B, 373).",
    color: "#1A3A5C",
  },
  {
    icon: Briefcase,
    title: "HR & People Operations",
    body: "Aggregate, anonymised insight into team workload, interruption hotspots, and refocus cost. Use it to make the case for healthier defaults - never to surveil individuals.",
    note: "After an interruption, full task re-engagement averages 23 minutes 15 seconds - a frequently cited figure from field studies of information workers (Mark, G. et al., 2008, CHI '08, ACM, 107–110).",
    color: "#2C6BAC",
  },
  {
    icon: BookOpen,
    title: "Students",
    body: "Academic deep work is a skill - and increasingly under siege. WYLO scaffolds it without locking you out of the tools you actually need.",
    note: "Higher in-class multitasking and digital distraction predict lower GPA and reduced comprehension across multiple studies (Junco, R. & Cotten, S.R., 2012, Computers & Education, 59(2), 505–514).",
    color: "#BA7517",
  },
];

export default function Individuals() {
  return (
    <MarketingLayout>
      <section className="pt-28 pb-12 max-w-7xl mx-auto px-6 lg:px-10" data-testid="individuals-hero">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Using WYLO · Individuals</p>
        <h1 className="font-display text-5xl lg:text-7xl tracking-tighter text-[#1A1A2E] leading-[0.95] max-w-3xl">
          For everyone who works.<br />
          <span className="text-[#1A3A5C]">With empathy for the realities of attention.</span>
        </h1>
        <p className="mt-7 text-lg text-[#555568] leading-relaxed max-w-2xl">
          We built WYLO because the productivity category had forgotten the people inside it.
          Every claim below is grounded in peer-reviewed research. The full reference list lives in our{" "}
          <Link to="/resources#research" className="text-[#2C6BAC] font-semibold hover:underline" data-testid="individuals-research-link">Resources</Link>.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-5 pb-32">
        {CASES.map((c, i) => (
          <article
            key={i}
            data-testid={`use-case-${i}`}
            className="rounded-xl border border-[#E8ECF2] bg-white p-7 hover:-translate-y-1 transition-transform flex flex-col"
          >
            <div className="h-12 w-12 rounded-md flex items-center justify-center text-white" style={{ background: c.color }}>
              <c.icon size={20} />
            </div>
            <h3 className="font-display text-xl font-bold text-[#1A1A2E] mt-5">{c.title}</h3>
            <p className="text-sm text-[#555568] mt-3 leading-relaxed">{c.body}</p>
            <div className="mt-5 pt-4 border-t border-[#E8ECF2] flex-1">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-2">
                What the research says
              </p>
              <p className="text-xs text-[#555568] leading-relaxed italic">{c.note}</p>
            </div>
          </article>
        ))}

        <div className="rounded-xl bg-[#1A3A5C] text-white p-7 flex flex-col justify-center" data-testid="note-on-language">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E8ECF2]/70 font-semibold mb-4">A note on language</p>
          <h3 className="font-display text-2xl lg:text-3xl tracking-tighter leading-[1.1]">
            Every use case here is treated with the seriousness it deserves.
          </h3>
          <p className="text-[#E8ECF2]/80 mt-5 text-sm leading-relaxed">
            We don't trivialise neurodivergence, postpartum cognition, menopause, or caregiving load. The research is well-established,
            and the people living these experiences deserve tools that meet them where they are. WYLO is a tool - not a diagnosis,
            not a cure, not a substitute for clinical care. It is a structural support that respects you.
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
